import { useEffect, useRef, type ReactElement } from 'react';
import { Button } from '@/components/ui/Button';
import type { PageLayout } from '@/lib/use-page-layout';

/*
 * The "Edit layout" switch for a `usePageLayout` layout, for a PageHeader's
 * actions slot (contract owner 2026-10-20).
 *
 * Not editing it is one quiet ghost button, so the header reads as it did.
 * Editing it is Reset (disabled — with the reason on its wrapper Tooltip —
 * while the layout is already the built-in one) and a primary Done. Reset only
 * appears while editing: resetting a layout whose frames you cannot see is a
 * trap. `compact` makes every control icon-only with a name Tooltip, for tight
 * header rows.
 *
 * Focus follows the swap: Edit layout → Done, Done → Edit layout, and Reset
 * (which then disables itself) → Done, so a keyboard user is never dropped on
 * `<body>`.
 *
 * A failed save shows "Layout not saved." beside the buttons; the engine keeps
 * the edit on screen either way.
 */

export interface LayoutToolbarProps {
  layout: PageLayout;
  compact?: boolean;
}

type FocusTarget = 'edit' | 'done';

export function LayoutToolbar({ layout, compact = false }: LayoutToolbarProps): ReactElement {
  const editRef = useRef<HTMLButtonElement>(null);
  const doneRef = useRef<HTMLButtonElement>(null);
  const focusNext = useRef<FocusTarget | null>(null);
  const { editing, isDefault, saveError } = layout;

  useEffect(() => {
    const target = focusNext.current;
    if (!target) return;
    focusNext.current = null;
    (target === 'edit' ? editRef.current : doneRef.current)?.focus();
  });

  const error = saveError ? (
    <span role="status" className="text-xs text-danger">
      Layout not saved.
    </span>
  ) : null;

  if (!editing) {
    return (
      <div className="flex shrink-0 items-center gap-2">
        {error}
        <Button
          ref={editRef}
          variant="ghost"
          iconOnly={compact}
          aria-label={compact ? 'Edit layout' : undefined}
          leadingIcon={<LayoutIcon />}
          onClick={() => {
            focusNext.current = 'done';
            layout.setEditing(true);
          }}
        >
          Edit layout
        </Button>
      </div>
    );
  }

  const resetReason = 'Already the built-in layout';
  return (
    <div className="flex shrink-0 items-center gap-2">
      {error}
      <Button
        variant="secondary"
        iconOnly={compact}
        aria-label={compact ? 'Reset layout' : undefined}
        tooltip={isDefault ? (compact ? `Reset layout: ${resetReason.toLowerCase()}` : resetReason) : undefined}
        disabled={isDefault}
        leadingIcon={<ResetIcon />}
        onClick={() => {
          focusNext.current = 'done';
          layout.reset();
        }}
      >
        Reset
      </Button>
      <Button
        ref={doneRef}
        variant="primary"
        iconOnly={compact}
        aria-label={compact ? 'Done editing layout' : undefined}
        leadingIcon={<CheckIcon />}
        onClick={() => {
          focusNext.current = 'edit';
          layout.setEditing(false);
        }}
      >
        Done
      </Button>
    </div>
  );
}

/* Lucide paths, inlined so this component owns its glyphs. */
const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  className: 'h-4 w-4 shrink-0',
} as const;

/** layout-dashboard */
function LayoutIcon() {
  return (
    <svg {...stroke}>
      <rect width="7" height="9" x="3" y="3" rx="1" />
      <rect width="7" height="5" x="14" y="3" rx="1" />
      <rect width="7" height="9" x="14" y="12" rx="1" />
      <rect width="7" height="5" x="3" y="16" rx="1" />
    </svg>
  );
}

/** rotate-ccw */
function ResetIcon() {
  return (
    <svg {...stroke}>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}

/** check */
function CheckIcon() {
  return (
    <svg {...stroke}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
